import * as React from 'react';
import { BusinessGroup } from 'components/search/utils';
import { Button, IconButton } from 'omakase';
import { ChronoUnit, LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { DEFAULT_MAX_SEARCH_PARTY_SIZE_DISPLAY } from 'utils/searchBar';
import { DatePickerField, SelectField } from 'ui';
import { FormatDateOptions } from 'react-intl';
import { IconSearch } from '@tock/icons';
import {
  IllustrationDelivery,
  IllustrationDineIn,
  IllustrationEvents,
  IllustrationEverything,
  IllustrationTakeout,
  IllustrationWine,
} from 'ui/illustrations';
import { PatronLocationContainer } from 'components/patron-location/PatronLocationContainer';
import { PatronLocationTubeSearchInput } from 'components/patron-location/PatronLocationTubeSearchInput';
import {
  QuickFilterSelectionType,
  getDateRangeResolver,
  quickFilterDisplayValueForValue,
} from 'utils/search/quickFilterUtils';
import { Y_OFFSET_SIZE_PX } from 'consumer-page-widgets/SearchLauncherWidget';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer } from '@tock/coretypes';
import { getDisplayedTimes } from 'utils/timeUtils';
import { makeStyles } from '@material-ui/core/styles';
import { stringify } from '@tock/query-string';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import Anchor from 'components/Anchor';
import clsx from 'clsx';

export const CITY_SEARCH_GA_CATEGORY = 'City Search Launcher - Rebrand';
export const GROUP_SEARCH_GA_CATEGORY = 'Group Search Launcher - Rebrand';
const DEFAULT_EVENTS_QUICK_FILTER_TYPE: QuickFilterSelectionType = 'upcoming';

export const useStyles = makeStyles((theme) => {
  const labelTop = 16;
  const labelTopLarge = 20;
  const labelLeft = 16;
  return {
    root: {
      [theme.breakpoints.up('md')]: {
        alignItems: 'center',
        display: 'flex',
        margin: 0,
        width: '100%',
      },
    },
    datePicker: {
      // rip this out when we make it have the same construction as a select. the MuiInputBase-input is not structured the same way as others.
      '& .MuiInputBase-root': {
        cursor: 'pointer',
      },
    },
    select: {
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(1),
        width: '100% !important',
      },
      '& .MuiInputBase-root .MuiSelect-root, & .MuiInputBase-input': {
        paddingLeft: labelLeft,
      },
      '& .MuiInputLabel-root': {
        color: theme.colors.fgDefault,
        zIndex: theme.zIndex.bump * 2,
        '&.Mui-focused': {
          color: theme.colors.fgPrimaryBase,
        },
      },
      '&.MuiFormControl-root': {
        '& .MuiButtonBase-root.Mui-focusVisible': {
          color: theme.colors.fgPrimaryBase,
        },
        '& .MuiInputLabel-root, & .MuiSelect-iconFilled': {
          color: theme.colors.fgDefault,
          zIndex: theme.zIndex.bump * 2,
          '&.Mui-focused': {
            color: theme.colors.fgPrimaryBase,
          },
        },
        '& .MuiIconButton-root svg': {
          color: theme.colors.fgDefault,
        },
        '& .MuiInputBase-root.Mui-focused svg': {
          color: theme.colors.fgPrimaryBase,
        },
        '& .MuiInputBase-root.Mui-focused': {
          backgroundColor: theme.colors.bgLighter,
          borderColor: theme.colors.borderPrimaryBase,
          '&, & .MuiInputBase-input': {
            '&, &[disabled]': {
              color: theme.colors.fgPrimaryBase,
              '-webkit-text-fill-color': theme.colors.fgPrimaryBase,
            },
          },
          [theme.breakpoints.up('sm')]: {
            outline: `2px solid ${theme.colors.borderPrimaryBase}`,
            position: 'relative',
            zIndex: theme.zIndex.bump,
          },
        },
      },
      '& .MuiInputBase-root:not(:hover):not(.Mui-focused)': {
        borderColor: theme.colors.borderDefault,
      },
      '& .MuiInputBase-root, & .MuiInputBase-root.Mui-focused': {
        borderWidth: 1,
        borderBottomWidth: 2,

        '& .MuiSelect-select:focus, & .MuiInputBase-root': {
          backgroundColor: theme.colors.bgLighter,
          color: theme.colors.fgPrimaryBase,
          '& + .MuiSelect-iconFilled': {
            color: theme.colors.fgPrimaryBase,
          },
          [theme.breakpoints.up('sm')]: {
            outline: `2px solid ${theme.colors.borderPrimaryBase}`,
            position: 'relative',
            zIndex: theme.zIndex.bump,
          },
        },
        [theme.breakpoints.down('sm')]: {
          border: 'none',
          borderBottom: `1px solid ${theme.colors.borderDefault}`,
          '& .MuiSelect-iconFilled': {
            right: 12,
          },
        },
        [theme.breakpoints.up('md')]: {
          borderWidth: 0,
          borderBottomWidth: 0,
        },
        '&, &.Mui-focused, & .MuiSelect-root': {
          borderRadius: 0,
        },
      },
      '& .MuiInputLabel-filled.MuiInputLabel-shrink': {
        transform: `translate(${labelLeft}px, ${labelTop}px) scale(0.875)!important`,
        [theme.breakpoints.up('md')]: {
          transform: `translate(${labelLeft}px, ${labelTopLarge}px) scale(0.875)!important`,
        },
      },
    },
    selectExperience: {
      '& .MuiInputBase-adornedStart': {
        paddingLeft: 0,
      },
      '& .MuiInputBase-root .MuiSelect-root': {
        paddingLeft: labelLeft,
      },
      '& .MuiInputLabel-filled.MuiInputLabel-shrink': {
        transform: `translate(${labelLeft}px, ${labelTop}px) scale(0.875)!important`,
      },
      '& .MuiInputBase-root > svg:first-child': {
        height: theme.spacing(8),
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        top: 2,
        width: theme.spacing(8),
        zIndex: theme.zIndex.bump * 2,
        [theme.breakpoints.up('md')]: {
          left: theme.spacing(3),
          top: theme.spacing(1),
        },
      },
      [theme.breakpoints.up('md')]: {
        '& .MuiInputBase-root .MuiSelect-root': {
          paddingLeft: theme.spacing(3) + labelLeft,
        },
        '& .MuiInputLabel-filled.MuiInputLabel-shrink': {
          transform: `translate(${
            theme.spacing(3) + labelLeft
          }px, ${labelTopLarge}px) scale(0.875)!important`,
        },
        '& .MuiInputBase-root, & .MuiInputBase-root.Mui-focused': {
          '&, &.Mui-focused, & .MuiSelect-root': {
            borderBottomLeftRadius: Y_OFFSET_SIZE_PX,
            borderTopLeftRadius: Y_OFFSET_SIZE_PX,
          },
        },
      },
    },

    actionTextRow: {
      display: 'flex',
      marginTop: theme.spacing(1.5),
    },
    actionText: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },

    showDesktop: {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    hideDesktop: {
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },

    circleButton: {
      height: theme.spacing(7),
      minWidth: theme.spacing(7),
      padding: 0,
      width: theme.spacing(7),
    },

    field: {
      borderRadius: 0,
      boxSizing: 'border-box',
      fontWeight: theme.typography.fontWeightMedium,
      height: theme.spacing(8.5),
      [theme.breakpoints.up('md')]: {
        height: `calc(${theme.spacing(10)}px - 2px)`,
      },
    },
    gridLastChild: {
      [theme.breakpoints.up('md')]: {
        flexDirection: 'column',
        justifyContent: 'center',
        paddingLeft: theme.spacing(1),
        paddingRight: `${theme.spacing(1.5)}px!important`,
      },
    },
    gridItemPrimary: {
      [theme.breakpoints.up('md')]: {
        borderRight: `1px solid ${theme.colors.borderDefault}`,
        flex: '0 0 25%',
        width: '25%',
        padding: '0 !important',
      },
    },
    gridItemAuto: {
      [theme.breakpoints.up('md')]: {
        padding: '0 !important',
        flex: '1 1 20%',
      },
    },
    gridItem: {
      [theme.breakpoints.up('md')]: {
        flex: '1 0 15%',
        minWidth: 160,
        padding: '0 !important',
        width: 'auto',
        position: 'relative',
        '&::after': {
          borderLeft: `1px solid ${theme.colors.borderDefault}`,
          bottom: theme.spacing(2),
          content: '""',
          left: 0,
          position: 'absolute',
          top: theme.spacing(2),
        },
      },
    },
    // The gridItemGuestNavNotEnabled class ensures that, if the new nav isn't enabled and the location field
    // doesn't show, maintain the existing (pre-nav-rebrand) styling for the search launcher fields
    gridItemGuestNavNotEnabled: {
      [theme.breakpoints.up('lg')]: {
        minWidth: 180,
      },
    },
  };
});

export type SearchLauncherType =
  'all' | 'delivery' | 'events' | 'pickup' | 'reservations' | 'wineries';

interface TypeData {
  illustration?: React.ReactNode;
  label: string;
  type?: consumer.OfferingSearchType;
  businessType?: string;
}

const SearchLauncherTypeToTypeData: { [K in SearchLauncherType]: TypeData } = {
  all: {
    label: 'Everything',
    type: 'ALL_EXPERIENCES',
    illustration: <IllustrationEverything color="primary" />,
  },
  delivery: {
    label: 'Delivery',
    type: 'ORDER_DELIVERY',
    illustration: <IllustrationDelivery color="primary" />,
  },
  events: { label: 'Events', type: 'EVENT', illustration: <IllustrationEvents color="primary" /> },
  pickup: {
    label: 'Pickup',
    type: 'ORDER_PICKUP',
    illustration: <IllustrationTakeout color="primary" />,
  },
  reservations: {
    label: 'Dine in',
    type: 'DINE_IN_EXPERIENCES',
    illustration: <IllustrationDineIn color="primary" />,
  },
  wineries: {
    label: 'Wineries',
    businessType: 'winery',
    illustration: <IllustrationWine color="primary" />,
  },
};

export interface BuildSearchUrlOptions {
  businessGroup?: BusinessGroup;
  metroArea?: consumer.IMetroArea;
  date: LocalDate;
  dateEnd?: LocalDate;
  guests: string;
  time?: LocalTime;
  type?: consumer.OfferingSearchType;
  searchSuffix?: string;
  businessType?: string;
}

// TODO: consolidate this with search code in SearchRoute
export const buildSearchUrl = ({
  businessGroup,
  metroArea,
  date,
  dateEnd,
  guests,
  time,
  type,
  searchSuffix,
  businessType,
}: BuildSearchUrlOptions) => {
  const excludePartySize = type === 'EVENT' || type === 'ORDER_DELIVERY' || type === 'ORDER_PICKUP';
  const excludeTime = type === 'EVENT';
  let queryStringParams: Record<string, string | undefined> = {
    date: date.toString(),
    dateEnd: type === 'EVENT' ? dateEnd?.toString() : undefined,
    size: excludePartySize ? undefined : guests,
    time: excludeTime ? undefined : time ? time.toString() : undefined,
    type,
    businessType,
  };
  if (businessGroup) {
    queryStringParams = {
      ...queryStringParams,
      businessGroupId: `${businessGroup.id}`,
    };
  } else if (metroArea) {
    queryStringParams = {
      ...queryStringParams,
      city: `${metroArea.name}`,
    };
  }

  const baseUrl = metroArea
    ? `/city/${metroArea.slug}${searchSuffix ?? ''}`
    : businessGroup
      ? `/group/${businessGroup.slug}${searchSuffix ?? ''}`
      : '';

  return `${baseUrl}?${stringify(queryStringParams)}`;
};

interface SearchLauncherProps {
  businessGroup?: BusinessGroup;
  experienceTypes: SearchLauncherType[];
  metroArea?: consumer.IMetroArea;
  onChange: (value: SearchLauncherType) => void;
  type: SearchLauncherType;
}

const SearchLauncher: React.FunctionComponent<SearchLauncherProps> = ({
  businessGroup,
  experienceTypes,
  metroArea,
  onChange,
  type,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const classes = useStyles();
  const today = LocalDateTime.now();
  const todayDate = today.toLocalDate();
  const calendarEndDateLimit = todayDate.plusYears(1);
  const [showTime, setShowTime] = React.useState<boolean>(false);
  const defaultDateTime = today.truncatedTo(ChronoUnit.HOURS).plusHours(1);
  const [guests, setGuests] = React.useState<string>('2');
  const [date, setDate] = React.useState<LocalDate>(defaultDateTime.toLocalDate());
  const [dateEnd, setDateEnd] = React.useState<LocalDate | undefined>(undefined);
  const [eventQuickFilterSelection, setEventQuickFilterSelection] =
    React.useState<QuickFilterSelectionType>(DEFAULT_EVENTS_QUICK_FILTER_TYPE);
  const [isTimeDirty, setIsTimeDirty] = React.useState(false);

  const isToday = todayDate.equals(date);
  const locale = metroArea?.locale || 'en-US';
  const displayedTimes = date ? getDisplayedTimes(date, LocalTime.now(), locale, 30) : [];
  const defaultTime = isToday ? LocalTime.parse(displayedTimes[0].value) : LocalTime.parse('19:00');
  const [time, setTime] = React.useState<LocalTime>(defaultTime);
  const asapTime = isToday ? displayedTimes[0].value : undefined;

  React.useEffect(() => {
    setShowTime(true);
  }, []);

  const typeData = SearchLauncherTypeToTypeData[type];
  const url = buildSearchUrl({
    businessGroup,
    metroArea,
    date,
    dateEnd,
    guests,
    time,
    type: typeData.type,
    businessType: typeData.businessType,
    searchSuffix: '/search',
  });

  const guestOptions: string[] = [];
  for (let i = 1; i <= DEFAULT_MAX_SEARCH_PARTY_SIZE_DISPLAY; i++) {
    guestOptions.push(i.toString());
  }

  const doTracking = () => {
    amplitudeSendEvent('Click structured reservation search', {
      businessGroupName: businessGroup?.name,
      date: date.toString(),
      time: time.toString(),
      offeringSearchType: typeData.type,
      partySize: Number(guests),
      // location is a user property, no need to track
    });
    if (!window.analytics) {
      return;
    }
    window.analytics.sendEvent(
      businessGroup ? GROUP_SEARCH_GA_CATEGORY : CITY_SEARCH_GA_CATEGORY,
      'Search',
      typeData.type
    );
  };

  const handleChangeType = (selectedType: SearchLauncherType) => {
    onChange(selectedType);
    if (selectedType === 'events') {
      handleChangeEventsQuickFilter(DEFAULT_EVENTS_QUICK_FILTER_TYPE);
    }
  };

  const handleChangeDate = (newDate: LocalDate) => {
    // if the current set date is today and the set time is the default, set the time for the new day to 7pm
    // else if we're switching to today and we haven't touched the time, switch the time back to asap
    if (isToday && !newDate.equals(date) && time.equals(defaultTime)) {
      setTime(LocalTime.parse('19:00'));
    } else if (todayDate.equals(newDate) && !isTimeDirty) {
      const newDateAsapTime = LocalTime.parse(
        getDisplayedTimes(newDate, LocalTime.now(), locale, 30)[0].value
      );
      setTime(newDateAsapTime);
    }

    setDate(newDate);
  };

  const handleChangeTime = (newTime: LocalTime) => {
    setIsTimeDirty(true);
    setTime(newTime);
  };

  const thisYear = date.year() === today.year();
  const dateFormat: FormatDateOptions = {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
    year: thisYear ? undefined : 'numeric',
  };

  const handleChangeEventsQuickFilter = (selectionType: QuickFilterSelectionType) => {
    const dates = getDateRangeResolver().get(selectionType);

    if (dates?.().newStartDate) {
      setDate(dates().newStartDate);
    }

    if (dates?.().newEndDate) {
      setDateEnd(dates().newEndDate);
    }

    setEventQuickFilterSelection(selectionType);
  };

  const getClassNameForPosition = (position: number) => {
    return clsx({
      [classes.gridItem]: position > 1,
      [classes.gridItemPrimary]: position === 0,
      [classes.gridItemAuto]: position === 1,
    });
  };

  const renderTypes = (position: number) => (
    <SelectField
      key="type"
      label="Reservation type"
      name="type"
      id={`${type}-city-search-type`}
      value={type}
      onChange={(e) => {
        const selectedType = e.target.value as SearchLauncherType;
        handleChangeType(selectedType);
      }}
      className={clsx(getClassNameForPosition(position), classes.select, classes.selectExperience)}
      SelectProps={{
        inputProps: { className: classes.field, 'data-testid': 'city-search-type-select' },
      }}
      InputLabelProps={{ disableAnimation: true, shrink: true }}
    >
      {experienceTypes.map((t) => (
        <option value={t} key={t}>
          {SearchLauncherTypeToTypeData[t].label}
        </option>
      ))}
    </SelectField>
  );

  const renderDate = (position: number) => (
    <DatePickerField
      key="date"
      label="Date"
      name="date"
      id={`${type}-city-search-date`}
      format={dateFormat}
      value={date}
      onChange={handleChangeDate}
      className={clsx(getClassNameForPosition(position), classes.select, classes.datePicker)}
      InputProps={{ className: classes.field }}
      InputLabelProps={{ disableAnimation: true, shrink: true }}
      showsCalendarIcon={false}
      usePseudoElementDisplay={true}
      DatePickerProps={{ latestSelectableDate: calendarEndDateLimit, numberOfMonths: { xs: 13 } }}
    />
  );

  const renderGuests = (position: number) => (
    <SelectField
      key="guests"
      label="Party size"
      name="guests"
      id={`${type}-city-search-guests`}
      value={guests}
      onChange={(e) => setGuests(e.target.value as string)}
      className={clsx(getClassNameForPosition(position), classes.select)}
      SelectProps={{ inputProps: { className: classes.field } }}
      InputLabelProps={{ disableAnimation: true, shrink: true }}
    >
      {guestOptions.map((number) => (
        <option value={number} key={number}>
          {number} guest{number !== '1' && 's'}
        </option>
      ))}
    </SelectField>
  );

  const renderEventsQuickFilter = (position: number) => (
    <SelectField
      key="event-date"
      label="Event date"
      name="event-date"
      id={`${type}-city-search-event-date`}
      value={eventQuickFilterSelection}
      onChange={(e) => {
        const selectedQuickFilterType = e.currentTarget.value as QuickFilterSelectionType;
        handleChangeEventsQuickFilter(selectedQuickFilterType);
      }}
      className={clsx(getClassNameForPosition(position), classes.select)}
      SelectProps={{ inputProps: { className: classes.field } }}
      InputLabelProps={{ disableAnimation: true, shrink: true }}
    >
      {Object.entries(quickFilterDisplayValueForValue).map(([value, displayValue]) => {
        return (
          <option key={value} value={value}>
            {displayValue}
          </option>
        );
      })}
    </SelectField>
  );

  // To avoid passing down additional props through the layers of PatronLocationContainer,
  // we're bypassing the grid position from getClassNameForPosition and applying the
  // style (class GridItemAuto) explicitly within PatronLocationTubeSearchInput, assuming that
  // the position will always be 1.
  const renderLocation = () => (
    <PatronLocationContainer key="location" initialComponent={PatronLocationTubeSearchInput} />
  );

  const renderTime = (position: number) => {
    return (
      <SelectField
        key="time"
        label="Time"
        name="time"
        id={`${type}-city-search-time`}
        value={showTime ? time.toString() : ''}
        onChange={(e) => handleChangeTime(LocalTime.parse(e.target.value as string))}
        className={clsx(getClassNameForPosition(position), classes.select)}
        SelectProps={{ inputProps: { className: classes.field } }}
        InputLabelProps={{ disableAnimation: true, shrink: true }}
      >
        {showTime &&
          displayedTimes.map(({ label, value }) => (
            <option value={value} key={value}>
              {value === asapTime ? 'Now' : label}
            </option>
          ))}
        {!showTime && <option value=""></option>}
      </SelectField>
    );
  };

  let fields: React.ReactNode[] = [];
  switch (type) {
    case 'all':
    case 'wineries':
    case 'reservations': {
      fields = [renderTypes(0), renderLocation(), renderDate(2), renderTime(3), renderGuests(4)];
      break;
    }
    case 'events': {
      fields = [renderTypes(0), renderLocation(), renderEventsQuickFilter(3)];
      break;
    }
    case 'delivery':
    case 'pickup': {
      fields = [renderTypes(0), renderLocation(), renderDate(2), renderTime(3)];
      break;
    }
    default:
      assertNever(type);
  }

  return (
    <div className={classes.root}>
      {fields}
      <div className={clsx(classes.showDesktop, classes.gridLastChild)}>
        <IconButton
          onClick={doTracking}
          component={Anchor}
          href={url}
          aria-label="Search"
          size="xlarge"
          variant="primary"
          data-testid="in-city-search-button-desktop"
        >
          <IconSearch />
        </IconButton>
      </div>

      <div className={classes.hideDesktop}>
        <div className={classes.actionTextRow}>
          <Button
            onClick={doTracking}
            component={Anchor}
            href={url}
            size="large"
            variant="primary"
            iconStart={<IconSearch />}
            data-testid="in-city-search-button-mobile"
            fullWidth
          >
            Search
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchLauncher;
